import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/base/ComicCard";
import { comics, categories } from "@/mocks/comics";

const faqPreview = [
  {
    q: "策驰漫画网支持哪些阅读方式?",
    a: "网站支持流畅的翻页阅读与目录快速跳转,你可以逐页翻阅,也能一键跳转到任意章节,阅读体验更顺滑。",
  },
  {
    q: "阅读记录会自动保存吗?",
    a: "会的。你在策驰漫画网上的阅读进度会自动保存为阅读记录,即使更换设备,登录后也能接着上次的位置继续阅读。",
  },
  {
    q: "如何收藏喜欢的漫画并追更?",
    a: "在漫画详情页点击「收藏」即可加入书架,作品更新后你会在收藏列表看到最新章节提醒,追更方便。",
  },
  {
    q: "夜间模式在哪里开启?",
    a: "点击右上角的月亮图标即可一键切换到夜间模式,柔和的深色界面能有效减少夜间阅读时的光线刺激,保护眼睛。",
  },
];

const features = [
  {
    icon: "ri-book-open-line",
    title: "翻页阅读",
    desc: "点击或滑动即可翻页,顺滑无卡顿,沉浸式享受漫画内容。",
  },
  {
    icon: "ri-list-unordered",
    title: "目录跳转",
    desc: "章节目录一目了然,一键直达任意章节,再也不用手动翻找。",
  },
  {
    icon: "ri-star-line",
    title: "收藏追更",
    desc: "一键收藏心仪作品,更新第一时间提醒,不错过任何一话。",
  },
  {
    icon: "ri-history-line",
    title: "阅读记录",
    desc: "阅读进度自动同步,换设备也不丢进度,随时接着看。",
  },
  {
    icon: "ri-moon-line",
    title: "夜间模式",
    desc: "柔和深色护眼界面,夜间阅读也能保护双眼,久看不累。",
  },
  {
    icon: "ri-device-line",
    title: "多端同步",
    desc: "电脑、平板、手机无缝切换,书架与记录云端保持一致。",
  },
];

const testimonials = [
  {
    name: "小橘",
    role: "追更爱好者",
    text: "翻页真的很顺滑,读起来特别舒服。晚上用夜间模式看,眼睛不会那么累。",
  },
  {
    name: "阿泽",
    role: "玄幻书迷",
    text: "阅读记录同步太方便了,换了平板接着上次的进度看,一秒不差。",
  },
  {
    name: "默默",
    role: "学生党",
    text: "收藏功能好用,喜欢的作品更新了立刻就能看到,追更再也没错过。",
  },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  const hot = [...comics].sort((a, b) => b.follows - a.follows);
  const latest = comics.slice(6);
  const rankTop = hot.slice(0, 5);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative flex min-h-[520px] w-full items-center justify-center overflow-hidden md:min-h-[600px]">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20anime%20comic%20book%20collage%20background%20with%20floating%20panels%2C%20dynamic%20ink%20splashes%2C%20silhouetted%20heroes%2C%20warm%20orange%20and%20deep%20teal%20palette%2C%20dramatic%20nebula%20glow%2C%20artistic%20editorial%20illustration%2C%20cinematic%20atmosphere&width=1600&height=900&seq=hero-2026-comic&orientation=landscape"
          alt="策驰漫画网阅读平台背景图"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/40 to-background-950/70"></div>
        <div className="relative z-10 w-full px-4 py-16 text-center md:py-24">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur-sm">
            <i className="ri-flashlight-line text-accent-400"></i>
            畅享阅读 · 随时追更
          </p>
          <h1 className="mx-auto max-w-3xl font-heading text-4xl leading-tight text-background-50 md:text-5xl">
            策驰漫画网
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-background-100/90 md:text-lg">
            流畅翻页、目录跳转、收藏追更,阅读记录自动同步,夜间模式贴心护眼。随时随地,翻开你的下一部漫画。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
            >
              开始阅读
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/rank"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-bold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              查看排行榜
            </Link>
          </div>
        </div>
      </section>

      {/* Category nav */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
          {categories.map((c) => (
            <Link
              key={c}
              to={c === "全部" ? "/category" : `/category?type=${encodeURIComponent(c)}`}
              className="rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              {c}
            </Link>
          ))}
        </div>
      </section>

      {/* Hot recommendations */}
      <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">热门推荐</h2>
            <p className="mt-1 text-sm text-foreground-600">全网高人气漫画,一追就停不下来</p>
          </div>
          <Link
            to="/category"
            className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
          >
            更多
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {hot.slice(0, 10).map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Latest updates */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">最新更新</h2>
              <p className="mt-1 text-sm text-foreground-600">刚出炉的新章节,抢先看</p>
            </div>
            <Link
              to="/category"
              className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
            >
              全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {latest.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Ranking + features */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Rank sidebar */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-foreground-950">人气排行</h2>
              <Link to="/rank" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                完整榜单
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <ol className="mt-4 flex flex-col gap-3">
              {rankTop.map((comic, i) => (
                <li key={comic.id}>
                  <Link
                    to={`/comic/${comic.id}`}
                    className="flex items-center gap-3 rounded-xl border border-background-200/80 bg-background-50 p-2 transition-transform hover:-translate-y-0.5"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading text-lg ${
                        i === 0
                          ? "bg-accent-500 text-background-50"
                          : i === 1
                            ? "bg-primary-600 text-background-50"
                            : "bg-secondary-200 text-secondary-900"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <img
                      src={comic.cover}
                      alt={`${comic.title}封面`}
                      className="h-14 w-10 shrink-0 rounded-md object-cover object-top"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-foreground-950">{comic.title}</p>
                      <p className="text-xs text-foreground-500">{comic.category} · {comic.follows.toLocaleString()}人收藏</p>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-accent-600">
                      <i className="ri-star-fill"></i>
                      {comic.rating.toFixed(1)}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          {/* Feature highlights */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">为阅读而生</h2>
            <p className="mt-1 text-sm text-foreground-600">六大贴心功能,让看漫画变成一种享受</p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group flex items-start gap-4 rounded-xl border border-background-200/80 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 dark:bg-secondary-100">
                    <i className={`text-xl ${f.icon}`}></i>
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-950">{f.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-foreground-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <h2 className="text-center font-heading text-2xl text-foreground-950 md:text-3xl">读者口碑</h2>
          <p className="mt-1 text-center text-sm text-foreground-600">来自广大漫画爱好者的真实评价</p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-xl border border-background-200/80 bg-background-50 p-5">
                <div className="flex gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-6 text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 font-heading text-background-50">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <h2 className="text-center font-heading text-2xl text-foreground-950 md:text-3xl">常见问题</h2>
        <p className="mt-1 text-center text-sm text-foreground-600">关于阅读体验的疑问,这里都有答案</p>
        <div className="mt-6 flex flex-col gap-3">
          {faqPreview.map((item, i) => (
            <div key={item.q} className="rounded-xl border border-background-200/80 bg-background-50">
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                aria-expanded={openFaq === i}
              >
                <span className="text-sm font-bold text-foreground-950">{item.q}</span>
                <i
                  className={`shrink-0 text-lg text-primary-600 transition-transform ${
                    openFaq === i ? "rotate-180" : ""
                  } ri-add-line`}
                ></i>
              </button>
              {openFaq === i && (
                <p className="border-t border-background-200/70 px-5 py-4 text-sm leading-6 text-foreground-600">
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
        <p className="mt-5 text-center text-sm text-foreground-600">
          还有更多问题?
          <Link to="/faq" className="ml-1 text-primary-600 hover:text-primary-700">
            前往常见问题页
          </Link>
        </p>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-14 md:px-6">
        <div className="relative overflow-hidden rounded-2xl bg-primary-700 px-6 py-10 text-center md:py-14">
          <div className="absolute inset-0 bg-gradient-to-br from-accent-500/30 to-transparent"></div>
          <div className="relative z-10">
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">现在就开启你的漫画之旅</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-background-100/90 md:text-base">
              收藏追更、记录同步、夜间护眼,一切为你准备就绪。
            </p>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-7 py-3 text-sm font-bold text-primary-800 transition-colors hover:bg-accent-500 hover:text-background-50"
            >
              马上阅读
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqPreview.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </Layout>
  );
}